import React, { createContext, useContext, ReactNode, useState, useCallback, useEffect, useRef } from 'react';
import useClinicUser from 'shared/hooks/useClinicUser';
import { useUpdateUserSettings } from 'shared/hooks/useUpdateUserSettings';

export interface SummaryProvider {
  isSummaryButtonHidden: boolean;
  fetchSummary?: () => void;
  setFetchSummary: (fetchSummary: () => void) => void;
  updateSummaryButtonHidden: (value: boolean) => void;
}

const SummaryContext = createContext<SummaryProvider>({
  isSummaryButtonHidden: true,
  updateSummaryButtonHidden: (value: boolean): void => {
    throw new Error('updateSummaryButtonHidden: boolean => void ' + value);
  },
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  setFetchSummary: (fetchSummary: () => void): void => {
    throw new Error('setFetchSummary: (fetchSummary: () => void): void ');
  },
});

export const useSummaryProvider = (): SummaryProvider => useContext<SummaryProvider>(SummaryContext);

interface SummaryProviderProps {
  children: ReactNode;
}

const SummaryProvider = ({ children }: SummaryProviderProps): JSX.Element => {
  const [isSummaryButtonHidden, setIsSummaryButtonHidden] = useState<boolean>(true);
  const [fetchSummary, setFetchSummary] = useState<(() => void) | undefined>(undefined);
  const { update } = useUpdateUserSettings();
  const { clinicUser, refetchClinicUser } = useClinicUser();
  const hasBeenMounted = useRef<boolean>(false);
  const clinicUserSettings = clinicUser?.userSetting;

  useEffect(() => {
    if (clinicUserSettings?.hideSummaryButton !== undefined && !hasBeenMounted.current) {
      setIsSummaryButtonHidden(clinicUserSettings.hideSummaryButton);
      hasBeenMounted.current = true;
    }
  }, [clinicUserSettings?.hideSummaryButton]);

  const updateSummaryButtonHidden = useCallback(
    async (value: boolean): Promise<void> => {
      setIsSummaryButtonHidden(value);
      await update({
        hideSummaryButton: value,
      });
      refetchClinicUser();
    },
    [refetchClinicUser, update],
  );

  return (
    <SummaryContext.Provider
      value={{
        isSummaryButtonHidden,
        updateSummaryButtonHidden,
        fetchSummary,
        setFetchSummary,
      }}
    >
      {children}
    </SummaryContext.Provider>
  );
};

export default SummaryProvider;
